interface Props {
  open: boolean;
  onClose: () => void;
  /** 1-based question number currently being viewed. Commands with `introducedAt` ≤ currentOrder are shown. */
  currentOrder: number;
}

interface Cmd {
  symbol: string;
  name: string;
  effect: string;
  introducedAt: number; // benchmark question order that first uses/teaches this command
}

const COMMANDS: Cmd[] = [
  // Stack basics (Q1–Q4)
  { symbol: '@', name: 'terminate', effect: 'Stops the program.', introducedAt: 1 },
  { symbol: '0–9', name: 'push digit', effect: 'Each digit pushes itself as a separate number.', introducedAt: 1 },
  { symbol: '.', name: 'pop & print number', effect: 'Pops the top of the stack and prints it as a number, followed by a space.', introducedAt: 1 },
  { symbol: '(empty stack rule)', name: 'empty = 0', effect: 'Popping or peeking an empty stack always yields 0; no error.', introducedAt: 2 },
  { symbol: ':', name: 'duplicate', effect: 'Duplicates the top of the stack.', introducedAt: 3 },
  { symbol: '$', name: 'discard', effect: 'Pops the top of the stack and throws the value away.', introducedAt: 3 },
  // Arithmetic (Q5–Q7)
  { symbol: '+', name: 'add', effect: 'Pop a, pop b; push b + a.', introducedAt: 5 },
  { symbol: '-', name: 'subtract', effect: 'Pop a, pop b; push b − a.', introducedAt: 6 },
  { symbol: '*', name: 'multiply', effect: 'Pop a, pop b; push b × a.', introducedAt: 7 },
  { symbol: '/', name: 'integer divide', effect: 'Pop a, pop b; push integer quotient b ÷ a.', introducedAt: 7 },
  { symbol: '%', name: 'remainder (modulo)', effect: 'Pop a, pop b; push remainder of b ÷ a.', introducedAt: 7 },
  // Swap (Q8)
  { symbol: '\\', name: 'swap', effect: 'Swaps the top two values on the stack.', introducedAt: 8 },
  // Strings (Q9–Q10)
  { symbol: '"…"', name: 'string mode', effect: 'Each character between the quotes is pushed as its ASCII code.', introducedAt: 9 },
  { symbol: ',', name: 'pop & print char', effect: 'Pops the top of the stack and prints it as the ASCII character with that code (no trailing space).', introducedAt: 9 },
  // Navigation (Q11–Q12)
  { symbol: '>', name: 'go right', effect: 'Unconditional direction change: right.', introducedAt: 11 },
  { symbol: '<', name: 'go left', effect: 'Unconditional direction change: left.', introducedAt: 11 },
  { symbol: '^', name: 'go up', effect: 'Unconditional direction change: up.', introducedAt: 11 },
  { symbol: 'v', name: 'go down', effect: 'Unconditional direction change: down.', introducedAt: 11 },
  // Conditionals (Q13–Q14)
  { symbol: '→', name: 'cond. right (peek)', effect: 'Peek top: if non-zero go right, otherwise left.', introducedAt: 13 },
  { symbol: '←', name: 'cond. left (peek)', effect: 'Peek top: if non-zero go left, otherwise right.', introducedAt: 13 },
  { symbol: '↑', name: 'cond. up (peek)', effect: 'Peek top: if non-zero go up, otherwise down.', introducedAt: 13 },
  { symbol: '↓', name: 'cond. down (peek)', effect: 'Peek top: if non-zero go down, otherwise up.', introducedAt: 13 }
];

const TIPS = [
  { from: 1, text: 'Wine is **last-in, first-out**: the most recent push is the first to be popped.' },
  { from: 2, text: 'You can always pop from an empty stack — it just gives you `0`.' },
  { from: 5, text: 'Arithmetic operators always consume the top TWO values and push one result.' },
  { from: 6, text: 'Order matters for `-` and `/`. The rule is: pop `a`, pop `b`, push `b OP a`.' },
  { from: 9, text: 'String mode pushes characters in reading order — the last character ends up on top.' },
  { from: 10, text: 'To print a word in order, **write the string reversed** inside `"…"`.' },
  { from: 11, text: 'Wine programs are 2-D. Execution starts at the top-left going right. Blank cells are no-ops.' },
  { from: 13, text: 'Conditional arrows **peek** — they look at the top but don’t remove it.' },
  { from: 15, text: 'A counted loop usually contains: duplicate (`:`), do work, decrement (`1-`), then a conditional exit.' }
];

export function HelpPanel({ open, onClose, currentOrder }: Props) {
  if (!open) return null;
  const visibleCmds = COMMANDS.filter((c) => c.introducedAt <= currentOrder);
  const visibleTips = TIPS.filter((t) => t.from <= currentOrder);
  return (
    <div className="help-overlay" onClick={onClose}>
      <aside className="help-panel" onClick={(e) => e.stopPropagation()}>
        <header className="help-head">
          <h2>Wine — commands you've seen so far</h2>
          <button className="btn" onClick={onClose} aria-label="Close help">✕</button>
        </header>
        <p className="muted">
          Up through question {currentOrder}. New commands are introduced as the test progresses.
        </p>
        <table className="help-table">
          <thead>
            <tr>
              <th>Command</th>
              <th>Name</th>
              <th>Effect</th>
            </tr>
          </thead>
          <tbody>
            {visibleCmds.map((c) => (
              <tr key={`${c.symbol}-${c.name}`}>
                <td><code>{c.symbol}</code></td>
                <td>{c.name}</td>
                <td>{c.effect}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {visibleTips.length > 0 && (
          <>
            <h3>Tips</h3>
            <ul className="help-tips">
              {visibleTips.map((t, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: t.text.replace(/`([^`]+)`/g, '<code>$1</code>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
